import { useRef, useState } from "react";
import type { Attachment } from "@/types/models";
import { attachmentService } from "@/services/attachmentService";
import { handleApiError } from "@/services/api";

interface AttachmentUploadProps {
  attachments: Attachment[];
  onUpload: (attachment: Attachment) => void;
  onDelete: (id: number) => void;
  /** When set, each file gets move-earlier/later arrows; display order is the array order. */
  onMove?: (id: number, direction: "earlier" | "later") => void;
  disabled?: boolean;
  maxFiles?: number;
}

const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
];

export default function AttachmentUpload({
  attachments,
  onUpload,
  onDelete,
  onMove,
  disabled = false,
  maxFiles = 10,
}: AttachmentUploadProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so the same file can be re-selected
    if (fileRef.current) fileRef.current.value = "";

    setError(null);

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Only JPEG, PNG, and PDF files are allowed.");
      return;
    }

    if (file.size > MAX_SIZE) {
      setError("File is too large. Max 10 MB.");
      return;
    }

    if (attachments.length >= maxFiles) {
      setError(`Maximum ${maxFiles} files allowed.`);
      return;
    }

    setUploading(true);
    try {
      const attachment = await attachmentService.upload(file);
      onUpload(attachment);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await attachmentService.delete(id);
      onDelete(id);
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const isImage = (contentType: string) =>
    contentType === "image/jpeg" || contentType === "image/png";

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
        Receipts / Invoices
      </label>

      {/* File list */}
      {attachments.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {attachments.map((a, idx) => (
            <div
              key={a.id}
              className="group relative flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-1.5 dark:border-gray-700 dark:bg-gray-800"
            >
              {isImage(a.content_type) ? (
                <img
                  src={a.url}
                  alt={a.file_name}
                  className="h-14 w-14 rounded object-cover"
                />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded bg-error-50 dark:bg-error-900/20">
                  <span className="text-xs font-semibold text-error-600 dark:text-error-400">
                    PDF
                  </span>
                </div>
              )}
              <span className="max-w-[100px] truncate text-xs text-gray-600 dark:text-gray-400">
                {a.file_name}
              </span>
              {!disabled && onMove && (
                <span className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => onMove(a.id, "earlier")}
                    disabled={idx === 0}
                    className="rounded p-0.5 text-gray-400 hover:text-brand-500 disabled:opacity-30 disabled:hover:text-gray-400 dark:hover:text-brand-400"
                    title="Move earlier"
                    aria-label={`Move ${a.file_name} earlier`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 15.75l7.5-7.5 7.5 7.5"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => onMove(a.id, "later")}
                    disabled={idx === attachments.length - 1}
                    className="rounded p-0.5 text-gray-400 hover:text-brand-500 disabled:opacity-30 disabled:hover:text-gray-400 dark:hover:text-brand-400"
                    title="Move later"
                    aria-label={`Move ${a.file_name} later`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </button>
                </span>
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleDelete(a.id)}
                  className="ml-auto rounded p-0.5 text-gray-400 hover:text-error-500 dark:hover:text-error-400"
                  title="Remove"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      {!disabled && attachments.length < maxFiles && (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            onChange={handleFileChange}
            className="hidden"
            disabled={uploading}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:border-brand-400 hover:text-brand-600 disabled:opacity-50 dark:border-gray-600 dark:text-gray-400 dark:hover:border-brand-500 dark:hover:text-brand-400"
          >
            {uploading ? (
              <>
                <svg
                  className="h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Uploading...
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                  />
                </svg>
                Attach Receipt
              </>
            )}
          </button>
          <span className="ml-2 text-xs text-gray-400">
            {attachments.length}/{maxFiles}
          </span>
        </div>
      )}

      {error && (
        <p className="mt-1 text-xs text-error-500">{error}</p>
      )}
    </div>
  );
}
